import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  PencilIcon, 
  CalendarDaysIcon, 
  ChartBarIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

registerLocale('ru', ru);

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
  size?: string;
  screenSize?: string;
  screenResolution?: string;
  direction?: string;
  description?: string;
  isActive: boolean;
  isLED: boolean;
  powerConsumption?: string;
  notes?: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  imageUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
  createdAt: string;
  updatedAt: string;
}

interface PriceHistory {
  id: number;
  dailyPrice: string;
  monthlyPrice: string;
  effectiveFrom: string;
  isActive: boolean;
}

interface NewPriceFormData {
  dailyPrice: number;
  monthlyPrice: number;
  effectiveFrom: Date;
}

interface EditPriceFormData {
  dailyPrice: number;
  monthlyPrice: number;
  notes?: string;
}

const StructureDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth(); // Changed from isAllowed
  const [structure, setStructure] = useState<Structure | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [isSubmittingPrice, setIsSubmittingPrice] = useState(false);
  const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState<PriceHistory | null>(null);
  const [isSubmittingEditPrice, setIsSubmittingEditPrice] = useState(false);

  const { 
    register: registerPrice, 
    handleSubmit: handlePriceSubmit, 
    reset: resetPriceForm,
    control: priceControl,
    formState: { errors: priceErrors }
  } = useForm<NewPriceFormData>();

  const { 
    register: registerEditPrice, 
    handleSubmit: handleEditPriceSubmit, 
    formState: { errors: editPriceErrors },
    setValue: setEditValue, 
  } = useForm<EditPriceFormData>();

  const isCityboard = structure?.type === 'Ситиборд';

  const fetchStructureData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const structureResponse = await axios.get(`/structures/${id}`);
      setStructure(structureResponse.data);
      
      const priceHistoryResponse = await axios.get(`/structures/${id}/prices/history`);
      setPriceHistory(priceHistoryResponse.data);
    } catch (err) {
      console.error('Error fetching structure data:', err);
      setError('Ошибка при загрузке данных конструкции');
      toast.error('Ошибка при загрузке данных конструкции');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStructureData();
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [id]);
  
  const formatCurrency = (amount: number | null | string) => {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (numericAmount === null || isNaN(numericAmount)) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2
    }).format(numericAmount);
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  const handleAddPrice = () => {
    resetPriceForm({ 
      effectiveFrom: new Date(), 
      dailyPrice: isCityboard ? 0 : 0, 
      monthlyPrice: 0 
    });
    setIsPriceModalOpen(true);
    setError(null);
  };

  const onPriceSubmit = async (data: NewPriceFormData) => {
    if (!structure) return;
    setIsSubmittingPrice(true);
    setError(null);

    try {
      const priceData: any = {
        monthlyPrice: data.monthlyPrice,
        effectiveFrom: data.effectiveFrom.toISOString().split('T')[0],
      };

      if (!isCityboard) {
        priceData.dailyPrice = data.dailyPrice;
      }

      await axios.post(`/structures/${structure.id}/prices`, priceData);
      toast.success('Новая цена успешно добавлена');
      setIsPriceModalOpen(false);
      await fetchStructureData();
    } catch (err: any) {
      console.error('Error adding price:', err);
      const errorMsg = err.response?.data?.message || 'Ошибка при добавлении цены';
      setError(errorMsg);
      toast.error(errorMsg);
      setIsPriceModalOpen(true);
    } finally {
      setIsSubmittingPrice(false);
    }
  };

  const handleEditPrice = (price: PriceHistory) => {
    setEditingPrice(price);
    setEditValue('dailyPrice', parseFloat(price.dailyPrice) || 0);
    setEditValue('monthlyPrice', parseFloat(price.monthlyPrice) || 0);
    setIsEditPriceModalOpen(true);
    setError(null);
  };

  const onEditPriceSubmit = async (data: EditPriceFormData) => {
    if (!editingPrice) return;
    setIsSubmittingEditPrice(true);
    setError(null);

    try {
      const priceData: any = {
        monthlyPrice: data.monthlyPrice,
      };

      if (!isCityboard) {
        priceData.dailyPrice = data.dailyPrice;
      }

      await axios.put(`/prices/${editingPrice.id}`, priceData);
      toast.success('Цена успешно обновлена');
      setIsEditPriceModalOpen(false);
      setEditingPrice(null);
      await fetchStructureData();
    } catch (err: any) {
      console.error('Error updating price:', err);
      const errorMsg = err.response?.data?.message || 'Ошибка при обновлении цены';
      setError(errorMsg);
      toast.error(errorMsg);
      setIsEditPriceModalOpen(true);
    } finally {
      setIsSubmittingEditPrice(false);
    }
  };

  const handleDeletePrice = async (priceId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись цены? Это действие нельзя отменить.')) {
      setError(null);
      try {
        await axios.delete(`/prices/${priceId}`);
        toast.success('Запись цены успешно удалена');
        await fetchStructureData();
      } catch (err: any) {
        console.error('Error deleting price:', err);
        const errorMsg = err.response?.data?.message || 'Ошибка при удалении цены';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error && !structure) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">{error}</p>
        <Link to="/structures" className="mt-4 btn-outline inline-block">
          Вернуться к списку конструкций
        </Link>
      </div>
    );
  }
  
  if (!structure) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-700">Конструкция не найдена.</p>
        <Link to="/structures" className="mt-4 btn-outline inline-block">
          Вернуться к списку конструкций
        </Link>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader
        title={`Конструкция: ${structure.code}`}
        subtitle={structure.type}
        backLink="/structures"
      />
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error} 
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{structure.code}</h2>
                <div className="flex items-center space-x-2 mt-1 flex-wrap">
                  <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-1">
                    {structure.type}
                  </span>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${ 
                    structure.isActive 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  } mb-1`}>
                    {structure.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                  {structure.isLED && (
                     <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mb-1">
                        LED
                     </span>
                  )}
                </div>
              </div>
              
              <div className="text-left sm:text-right mt-3 sm:mt-0">
                <div className="text-gray-600 text-sm">Текущие цены:</div>
                {!isCityboard && (
                  <div className="text-lg font-semibold text-primary">
                    {formatCurrency(structure.currentDailyPrice)} / день
                  </div>
                )}
                <div className="text-lg font-semibold text-primary">
                  {formatCurrency(structure.currentMonthlyPrice)} / месяц
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-4 border-t pt-4">
              <div>
                <h3 className="text-gray-600 font-medium">Адрес / Местоположение</h3>
                <p className="text-gray-900">{structure.location || '-'}</p>
              </div>
              <div>
                <h3 className="text-gray-600 font-medium">Координаты (Шир., Долг.)</h3>
                <p className="text-gray-900">
                    {structure.latitude !== null && structure.longitude !== null 
                        ? `${structure.latitude?.toFixed(6)}, ${structure.longitude?.toFixed(6)}`
                        : '-'}
                </p>
              </div>
              {structure.isLED ? (
                <>
                  {structure.screenSize && (
                    <div>
                      <h3 className="text-gray-600 font-medium">Размер экрана</h3>
                      <p className="text-gray-900">{structure.screenSize}</p>
                    </div>
                  )}
                  {structure.screenResolution && (
                    <div>
                      <h3 className="text-gray-600 font-medium">Разрешение</h3>
                      <p className="text-gray-900">{structure.screenResolution}</p>
                    </div>
                  )}
                  {structure.powerConsumption && (
                    <div>
                      <h3 className="text-gray-600 font-medium">Потребление</h3>
                      <p className="text-gray-900">{structure.powerConsumption}</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {structure.size && (
                    <div>
                      <h3 className="text-gray-600 font-medium">Размеры</h3>
                      <p className="text-gray-900">{structure.size}</p>
                    </div>
                  )}
                </>
              )}
              {structure.direction && (
                 <div>
                   <h3 className="text-gray-600 font-medium">Направление / Сторона</h3>
                   <p className="text-gray-900">{structure.direction}</p>
                 </div>
              )}
             {structure.description && (
                <div className="md:col-span-2">
                  <h3 className="text-gray-600 font-medium">Описание</h3>
                  <p className="text-gray-900 whitespace-pre-wrap">{structure.description}</p>
                </div>
             )}
              {structure.notes && (
                <div className="md:col-span-2">
                  <h3 className="text-gray-600 font-medium">Заметки</h3>
                  <p className="text-gray-900 whitespace-pre-wrap">{structure.notes}</p>
                </div>
              )}
              <div className="md:col-span-2">
                   <h3 className="text-gray-600 font-medium">Добавлена / Обновлена</h3>
                   <p className="text-gray-900 text-sm">
                        Добавлена: {formatDate(structure.createdAt)} <br/>
                        Последнее обновление: {formatDate(structure.updatedAt)}
                   </p>
               </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {structure.imageUrl ? (
              <img src={structure.imageUrl} alt={`Изображение ${structure.code}`} className="w-full h-auto object-cover" />
            ) : (
              <div className="p-6 text-center text-gray-500">
                Нет изображения
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Действия</h3>
            <div className="space-y-2">
               <Link
                  to={`/availability?structureId=${id}`}
                  className="btn-outline w-full flex items-center justify-center"
                >
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Проверить доступность
                </Link>
                 <Link
                  to={`/reports/structure/${id}`}
                  className="btn-outline w-full flex items-center justify-center"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Статистика занятости
                </Link>
              {can('structures.edit') && ( // Changed from isAllowed
                <button
                  onClick={handleAddPrice}
                  className="btn-secondary w-full flex items-center justify-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Добавить Цену
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">История цен</h3>
            {priceHistory.length > 0 ? (
                 <div className="overflow-x-auto">
                     <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Дата начала действия
                                </th>
                                {!isCityboard && (
                                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Цена/день
                                  </th>
                                )}
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Цена/месяц
                                </th>
                                 <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Статус
                                </th>
                                {can('structures.view_price_history') && ( // Changed from isAllowed, new permission
                                   <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Действия
                                   </th>
                                )}
                            </tr>
                        </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
                            {priceHistory.map((price) => (
                                <tr key={price.id}>
                                    <td className="px-4 py-2 text-sm text-gray-700">{formatDate(price.effectiveFrom)}</td>
                                    {!isCityboard && (
                                      <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(price.dailyPrice)}</td>
                                    )}
                                    <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(price.monthlyPrice)}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${price.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {price.isActive ? 'Актуальная' : 'Архивная'}
                                        </span>
                                    </td>
                                     {can('structures.manage_price_history') && ( // Changed from isAllowed, new permission
                                       <td className="px-4 py-2 text-sm text-gray-500 space-x-2">
                                          <button
                                             onClick={() => handleEditPrice(price)}
                                             className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                             title="Редактировать цену"
                                           >
                                             <PencilSquareIcon className="h-4 w-4" />
                                          </button>
                                          <button
                                             onClick={() => handleDeletePrice(price.id)}
                                             className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                             title="Удалить цену"
                                          >
                                             <TrashIcon className="h-4 w-4" />
                                          </button>
                                       </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                 <p className="text-gray-500 italic">История цен пуста.</p>
            )}
        </div>

      <Modal 
        isOpen={isPriceModalOpen} 
        onClose={() => setIsPriceModalOpen(false)}
        title="Добавить новую цену"
      >
        <form onSubmit={handlePriceSubmit(onPriceSubmit)} className="space-y-4">
           {error && isPriceModalOpen && !isSubmittingPrice && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {error}
                </div>
            )}

           {!isCityboard && (
             <div>
               <label htmlFor="dailyPrice" className="form-label">Цена в день (KZT) *</label>
               <input
                 id="dailyPrice"
                 type="number"
                 step="any"
                 min="0"
                 className={`form-input ${priceErrors.dailyPrice ? 'border-red-500' : ''}`}
                 {...registerPrice('dailyPrice', { required: 'Цена в день обязательна', valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
               />
               {priceErrors.dailyPrice && <p className="mt-1 text-sm text-red-600">{priceErrors.dailyPrice.message}</p>}
             </div>
           )}
           
           <div>
             <label htmlFor="monthlyPrice" className="form-label">Цена в месяц (KZT) *</label>
             <input
               id="monthlyPrice"
               type="number"
               step="any"
               min="0"
               className={`form-input ${priceErrors.monthlyPrice ? 'border-red-500' : ''}`}
               {...registerPrice('monthlyPrice', { required: 'Цена в месяц обязательна', valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
             />
             {priceErrors.monthlyPrice && <p className="mt-1 text-sm text-red-600">{priceErrors.monthlyPrice.message}</p>}
           </div>
           
           <div>
             <label htmlFor="effectiveFrom" className="form-label">Дата начала действия *</label>
             <Controller
               control={priceControl}
               name="effectiveFrom"
               rules={{ required: "Дата обязательна" }}
               render={({ field, fieldState: { error } }) => (
                  <DatePicker
                     selected={field.value ? new Date(field.value) : null}
                     onChange={(date: Date | null) => field.onChange(date)}
                     locale={ru}
                     dateFormat="dd.MM.yyyy"
                     className={`form-input ${error ? 'border-red-500' : ''}`}
                     placeholderText="ДД.ММ.ГГГГ"
                     popperPlacement="top-start"
                  />
               )}
             />
             {priceErrors.effectiveFrom && <p className="mt-1 text-sm text-red-600">{priceErrors.effectiveFrom.message}</p>}
           </div>
           
           <div className="flex justify-end space-x-3 pt-4">
             <button 
               type="button" 
               onClick={() => setIsPriceModalOpen(false)} 
               className="btn-secondary"
               disabled={isSubmittingPrice}
             >
               Отмена
             </button>
             <button 
               type="submit" 
               className="btn-primary"
               disabled={isSubmittingPrice}
             >
               {isSubmittingPrice ? <LoadingSpinner /> : 'Добавить цену'}
             </button>
           </div>
        </form>
      </Modal>
      
      {editingPrice && (
        <Modal
            isOpen={isEditPriceModalOpen}
            onClose={() => { setIsEditPriceModalOpen(false); setEditingPrice(null); }}
            title={`Редактировать цену (от ${formatDate(editingPrice.effectiveFrom)})`}
        >
            <form onSubmit={handleEditPriceSubmit(onEditPriceSubmit)} className="space-y-4">
                 {error && isEditPriceModalOpen && !isSubmittingEditPrice && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                 )}

                {!isCityboard && (
                  <div>
                    <label htmlFor="editDailyPrice" className="form-label">Цена в день (KZT) *</label>
                    <input
                        id="editDailyPrice"
                        type="number"
                        step="any"
                        min="0"
                        className={`form-input ${editPriceErrors.dailyPrice ? 'border-red-500' : ''}`}
                        {...registerEditPrice('dailyPrice', { required: 'Цена в день обязательна', valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
                    />
                    {editPriceErrors.dailyPrice && <p className="mt-1 text-sm text-red-600">{editPriceErrors.dailyPrice.message}</p>}
                  </div>
                )}

                <div>
                    <label htmlFor="editMonthlyPrice" className="form-label">Цена в месяц (KZT) *</label>
                    <input
                        id="editMonthlyPrice"
                        type="number"
                        step="any"
                        min="0"
                        className={`form-input ${editPriceErrors.monthlyPrice ? 'border-red-500' : ''}`}
                        {...registerEditPrice('monthlyPrice', { required: 'Цена в месяц обязательна', valueAsNumber: true, min: { value: 0, message: 'Цена не может быть отрицательной' } })}
                    />
                    {editPriceErrors.monthlyPrice && <p className="mt-1 text-sm text-red-600">{editPriceErrors.monthlyPrice.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <button
                        type="button"
                        onClick={() => { setIsEditPriceModalOpen(false); setEditingPrice(null); }}
                        className="btn-secondary"
                        disabled={isSubmittingEditPrice}
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={isSubmittingEditPrice}
                    >
                        {isSubmittingEditPrice ? <LoadingSpinner /> : 'Сохранить изменения'}
                    </button>
                </div>
            </form>
        </Modal>
      )}
      
    </div>
  );
};

export default StructureDetails;
